import React, { type ReactNode } from 'react';

import { type MessageDescriptor, useIntl } from 'react-intl-next';

import AtlassianIntelligenceIcon from '@atlaskit/icon/core/atlassian-intelligence';
import AutomationIcon from '@atlaskit/icon/core/automation';
import ChartTrendIcon from '@atlaskit/icon/core/chart-trend';
import CommentIcon from '@atlaskit/icon/core/comment';
import LockLoadedIcon from '@atlaskit/icon/core/lock-locked';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import PersonAddIcon from '@atlaskit/icon/core/person-add';
import { Flex, Stack, xcss } from '@atlaskit/primitives';

import { FeatureRow } from './feature-row';
import { messages } from './messages';

/**
 * Returns the 6 features we show based on the product. Right now, only Confluence and Jira are supported.
 *
 * @param isConfluence Confluence if true, Jira otherwise.
 * @param formatMessage A function that can provide translations.
 */
function getFeatureRows(
	isConfluence: boolean,
	formatMessage: (descriptor: MessageDescriptor) => string,
): ReactNode[] {
	const rows: ReactNode[] = [];

	rows.push(
		<FeatureRow
			name={formatMessage(messages.unlimitedUsers)}
			icon={<PersonAddIcon label="" color="currentColor" />}
			key="unlimitedUsers"
		/>,
	);

	rows.push(
		<FeatureRow
			name={
				isConfluence
					? formatMessage(messages.pagePermissions)
					: formatMessage(messages.userPermissions)
			}
			icon={<LockLoadedIcon label="" color="currentColor" />}
			key="permissions"
		/>,
	);

	if (isConfluence) {
		rows.push(
			<FeatureRow
				name={formatMessage(messages.freeGuestAccess)}
				icon={<PeopleGroupIcon label="" color="currentColor" />}
				key="confluenceFreeGuestAccess"
			/>,
		);

		rows.push(
			<FeatureRow
				name={formatMessage(messages.pageInsights)}
				icon={<ChartTrendIcon label="" color="currentColor" />}
				key="pageInsights"
			/>,
		);
	} else {
		rows.push(
			<FeatureRow
				name={formatMessage(messages.atlassianSupport)}
				icon={<CommentIcon label="" color="currentColor" />}
				key="atlassianSupport"
			/>,
		);

		rows.push(
			<FeatureRow
				name={formatMessage(messages.freeGuestAccess)}
				icon={<PeopleGroupIcon label="" color="currentColor" />}
				key="jiraFreeGuestAccess"
			/>,
		);
	}

	rows.push(
		<FeatureRow
			name={formatMessage(messages.atlassianIntelligence)}
			icon={<AtlassianIntelligenceIcon label="" color="currentColor" />}
			key="atlassianIntelligence"
		/>,
	);

	rows.push(
		<FeatureRow
			name={
				isConfluence
					? formatMessage(messages.automation)
					: formatMessage(messages.unlimitedAutomation)
			}
			icon={<AutomationIcon label="" color="currentColor" />}
			key="automation"
		/>,
	);

	return rows;
}

/**
 * Shows a stack of features.
 */
export const FeatureNameColumn = ({ isConfluence }: { isConfluence: boolean }) => {
	const { formatMessage } = useIntl();

	return (
		<Flex xcss={featureNameColumnStyles} justifyContent="center">
			<Stack space="space.200">{getFeatureRows(isConfluence, formatMessage)}</Stack>
		</Flex>
	);
};

const featureNameColumnStyles = xcss({
	width: '210px',
	height: '312px',
	backgroundColor: 'color.background.input',
	paddingTop: 'space.800',
});
